import React, { useState, useRef, useEffect } from 'react';
import { Button, Input, Card, Typography, Space, Avatar, Spin } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined } from '@ant-design/icons';
import './AIChat.css';

const { Text } = Typography;

const AIChat = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "¡Hola! Soy tu asistente de IA. ¿En qué puedo ayudarte hoy?",
      sender: 'ai',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Función para consultar la base de datos
  const queryDatabase = async (query) => {
    try {
      console.log('Enviando consulta:', query);
      const response = await fetch('https://api.mrcirp.com/public/db-info/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query })
      });
      
      console.log('Respuesta del servidor:', response.status, response.statusText);
      const data = await response.json();
      console.log('Datos recibidos:', data);
      return data;
    } catch (error) {
      console.error('Error consultando base de datos:', error);
      return { success: false, error: error.message };
    }
  };

  // Función para obtener información de la base de datos
  const getDatabaseInfo = async () => {
    try {
      const response = await fetch('https://api.mrcirp.com/public/db-info');
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error obteniendo información de BD:', error);
      return { success: false, error: error.message };
    }
  };

  // Función mejorada para procesar datos de la base de datos
  const processTableData = (data, tableName) => {
    if (!data || !data.success || !data.data) {
      return `No se encontraron datos en la tabla ${tableName}`;
    }

    const tableData = data.data;
    let result = `\n=== INFORMACIÓN DE LA TABLA ${tableName.toUpperCase()} ===\n`;
    
    // Agregar información del total
    if (tableData[`total_${tableName}`]) {
      result += `📊 Total de registros: ${tableData[`total_${tableName}`]}\n\n`;
    }

    // Procesar los registros
    const records = tableData[tableName] || tableData;
    if (Array.isArray(records) && records.length > 0) {
      result += `📋 Registros encontrados (mostrando los primeros ${Math.min(records.length, 10)}):\n\n`;
      
      records.slice(0, 10).forEach((record, index) => {
        // Filtrar solo los campos con nombres (no índices numéricos)
        const cleanRecord = {};
        Object.entries(record).forEach(([key, value]) => {
          if (isNaN(key) && value !== null && value !== undefined && value !== '') {
            cleanRecord[key] = value;
          }
        });
        
        // Formatear según el tipo de tabla
        if (tableName === 'cursos') {
          result += formatCourseRecord(cleanRecord, index + 1);
        } else if (tableName === 'usuarios') {
          result += formatUserRecord(cleanRecord, index + 1);
        } else if (tableName === 'pixels') {
          result += formatPixelRecord(cleanRecord, index + 1);
        } else if (tableName === 'lecciones') {
          result += formatLessonRecord(cleanRecord, index + 1);
        } else if (tableName === 'modulos') {
          result += formatModuleRecord(cleanRecord, index + 1);
        } else {
          result += formatGenericRecord(cleanRecord, index + 1);
        }
        
        result += '\n';
      });
      
      if (records.length > 10) {
        result += `\n📝 ... y ${records.length - 10} registros más.\n`;
      }
    } else {
      result += `❌ No hay registros en la tabla ${tableName}.\n`;
    }
    
    return result;
  };

  // Función para formatear registros de cursos
  const formatCourseRecord = (record, index) => {
    let formatted = `🎓 CURSO ${index}:\n`;
    formatted += `   📚 Nombre: ${record.nombrecurso || 'Sin nombre'}\n`;
    formatted += `   📝 Descripción: ${record.descripcion || 'Sin descripción'}\n`;
    if (record.fecha_creacion) {
      formatted += `   📅 Fecha de creación: ${record.fecha_creacion}\n`;
    }
    if (record.imagen) {
      formatted += `   🖼️ Imagen: ${record.imagen}\n`;
    }
    if (record.materiales) {
      formatted += `   📦 Materiales: ${record.materiales}\n`;
    }
    if (record.ejercicios) {
      formatted += `   💪 Ejercicios: ${record.ejercicios}\n`;
    }
    return formatted;
  };

  // Función para formatear registros de usuarios/landings
  const formatUserRecord = (record, index) => {
    let formatted = `👤 USUARIO ${index}:\n`;
    formatted += `   🏷️ Nombre: ${record.usuario || 'Sin nombre'}\n`;
    formatted += `   📄 Descripción: ${record.descripcion || 'Sin descripción'}\n`;
    if (record.email) {
      formatted += `   📧 Email: ${record.email}\n`;
    }
    if (record.telefono) {
      formatted += `   📱 Teléfono: ${record.telefono}\n`;
    }
    if (record.fecha_registro) {
      formatted += `   📅 Fecha de registro: ${record.fecha_registro}\n`;
    }
    return formatted;
  };

  // Función para formatear registros de pixels
  const formatPixelRecord = (record, index) => {
    let formatted = `🎯 PIXEL ${index}:\n`;
    formatted += `   🔗 Código: ${record.codigo || record.pixel_code || 'Sin código'}\n`;
    if (record.campana) {
      formatted += `   📢 Campaña: ${record.campana}\n`;
    }
    if (record.plataforma) {
      formatted += `   🌐 Plataforma: ${record.plataforma}\n`;
    }
    if (record.fecha_creacion) {
      formatted += `   📅 Fecha de creación: ${record.fecha_creacion}\n`;
    }
    return formatted;
  };

  // Función para formatear registros de lecciones
  const formatLessonRecord = (record, index) => {
    let formatted = `📖 LECCIÓN ${index}:\n`;
    formatted += `   📚 Título: ${record.titulo || record.nombre || 'Sin título'}\n`;
    formatted += `   📝 Descripción: ${record.descripcion || 'Sin descripción'}\n`;
    if (record.curso_id) {
      formatted += `   🎓 Curso ID: ${record.curso_id}\n`;
    }
    if (record.duracion) {
      formatted += `   ⏱️ Duración: ${record.duracion}\n`;
    }
    if (record.video_url) {
      formatted += `   🎥 Video: ${record.video_url}\n`;
    }
    return formatted;
  };

  // Función para formatear registros de módulos
  const formatModuleRecord = (record, index) => {
    let formatted = `📦 MÓDULO ${index}:\n`;
    formatted += `   📚 Nombre: ${record.nombre || 'Sin nombre'}\n`;
    formatted += `   📝 Descripción: ${record.descripcion || 'Sin descripción'}\n`;
    if (record.curso_id) {
      formatted += `   🎓 Curso ID: ${record.curso_id}\n`;
    }
    if (record.orden) {
      formatted += `   🔢 Orden: ${record.orden}\n`;
    }
    return formatted;
  };

  // Función para formatear registros genéricos
  const formatGenericRecord = (record, index) => {
    let formatted = `📋 REGISTRO ${index}:\n`;
    const importantFields = ['id', 'nombre', 'descripcion', 'fecha_creacion', 'fecha_registro'];
    const otherFields = Object.keys(record).filter(key => !importantFields.includes(key));
    
    [...importantFields, ...otherFields].forEach(key => {
      if (record[key]) {
        formatted += `   🏷️ ${key}: ${record[key]}\n`;
      }
    });
    
    return formatted;
  };

  // Función para consultar múltiples tablas según el contexto
  const getRelevantTableData = async (message) => {
    const queries = [];
    
    // Detectar qué tablas consultar basado en el mensaje
    if (/curso|cursos|academy|academia/i.test(message)) {
      queries.push({ endpoint: 'cursos', tableName: 'cursos' });
    }
    
    if (/leccion|lecciones/i.test(message)) {
      queries.push({ endpoint: 'lecciones', tableName: 'lecciones' });
    }
    
    if (/modulo|modulos/i.test(message)) {
      queries.push({ endpoint: 'modulos', tableName: 'modulos' });
    }
    
    if (/usuario|usuarios|landing|landings|cliente|clientes|pagina|paginas|aterrizaje/i.test(message)) {
      queries.push({ endpoint: 'usuarios', tableName: 'usuarios' });
    }
    
    if (/pixel|pixels|campana|campanas|tracking|publicidad/i.test(message)) {
      queries.push({ endpoint: 'pixels', tableName: 'pixels' });
    }

    // Si no se detectó ninguna tabla específica, consultar información general
    if (queries.length === 0) {
      queries.push({ endpoint: 'general', tableName: 'general' });
    }

    let contextData = '';
    
    for (const query of queries) {
      try {
        console.log(`Consultando ${query.endpoint}...`);
        let response;
        
        if (query.endpoint === 'general') {
          response = await fetch('https://api.mrcirp.com/public/db-info');
        } else {
          const url = `https://api.mrcirp.com/public/db-info/${query.endpoint}`;
          console.log(`URL completa: ${url}`);
          response = await fetch(url);
        }
        
        console.log(`Status de respuesta para ${query.endpoint}:`, response.status, response.statusText);
        
        // Verificar si la respuesta es exitosa
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        // Verificar el tipo de contenido
        const contentType = response.headers.get('content-type');
        console.log(`Content-Type para ${query.endpoint}:`, contentType);
        
        if (!contentType || !contentType.includes('application/json')) {
          // Si no es JSON, obtener como texto para debugging
          const textResponse = await response.text();
          console.log(`Respuesta no-JSON para ${query.endpoint}:`, textResponse);
          console.log(`Headers completos:`, Object.fromEntries(response.headers.entries()));
          throw new Error(`Respuesta no es JSON válido. Content-Type: ${contentType}. Contenido: ${textResponse.substring(0, 500)}...`);
        }
        
        const data = await response.json();
        console.log(`Respuesta de ${query.endpoint}:`, data);
        
        if (query.endpoint === 'general') {
          if (data.success && data.data) {
            contextData += `\n=== INFORMACIÓN GENERAL DE LA BASE DE DATOS ===\n`;
            contextData += `Base de datos: ${data.data.database_name}\n`;
            contextData += `Total de tablas: ${data.data.total_tables}\n`;
            contextData += `Tablas disponibles: ${data.data.tables.map(t => t.name).join(', ')}\n`;
          }
        } else {
          contextData += processTableData(data, query.tableName);
        }
        
      } catch (error) {
        console.error(`Error consultando ${query.endpoint}:`, error);
        contextData += `\nError al consultar ${query.endpoint}: ${error.message}\n`;
      }
    }
    
    return contextData;
  };

  // Función para ejecutar consultas SQL personalizadas
  const executeCustomQuery = async (sqlQuery) => {
    try {
      console.log('Ejecutando consulta personalizada:', sqlQuery);
      const response = await fetch('https://api.mrcirp.com/public/db-info/query', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: sqlQuery })
      });
      
      const data = await response.json();
      console.log('Resultado de consulta personalizada:', data);
      
      if (data.success && data.data && data.data.results) {
        return `\n=== RESULTADO DE CONSULTA PERSONALIZADA ===\n${JSON.stringify(data.data.results, null, 2)}\n`;
      } else {
        return `\nError en consulta personalizada: ${data.error || 'Error desconocido'}\n`;
      }
    } catch (error) {
      console.error('Error ejecutando consulta personalizada:', error);
      return `\nError ejecutando consulta: ${error.message}\n`;
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Detectar si el usuario quiere información de la base de datos
      const isDatabaseQuery = /base de datos|tabla|tablas|database|usuarios|datos|consulta|curso|cursos|leccion|lecciones|modulo|modulos|academy|academia|landing|landings|cliente|clientes|pagina|paginas|aterrizaje|pixel|pixels|campana|campanas|tracking|publicidad|informacion|info|mostrar|listar|ver|cuantos|cuantas/i.test(inputMessage);
      
      // Detectar si es una consulta SQL personalizada
      const isSQLQuery = /select|SELECT|from|FROM|where|WHERE|count|COUNT|sum|SUM|avg|AVG|max|MAX|min|MIN/i.test(inputMessage);
      
      let contextInfo = '';
      
      if (isDatabaseQuery) {
        console.log('Detectada consulta de base de datos, obteniendo información...');
        
        // Primero probar el endpoint de test
        try {
          console.log('Probando endpoint de test...');
          const testResponse = await fetch('https://api.mrcirp.com/public/db-info/test');
          const testData = await testResponse.json();
          console.log('Test endpoint funcionando:', testData);
        } catch (testError) {
          console.error('Error en test endpoint:', testError);
        }
        
        if (isSQLQuery) {
          // Si parece una consulta SQL, intentar ejecutarla
          console.log('Detectada consulta SQL, ejecutando...');
          contextInfo = await executeCustomQuery(inputMessage);
        } else {
          // Consulta normal de tablas
          contextInfo = await getRelevantTableData(inputMessage);
        }
      }

      const prompt = `Eres un asistente de IA especializado en la plataforma CIRP (Constructor de Ingresos Residuales Perpetuos) y CIRP Academy.

INFORMACIÓN IMPORTANTE SOBRE CIRP ACADEMY:
- La tabla "Cursos" contiene todos los cursos disponibles en CIRP Academy
- La tabla "Lecciones" contiene todas las lecciones de los cursos de CIRP Academy
- La tabla "modulos" se relaciona con Cursos y Lecciones (estructura modular de los cursos)
- CIRP Academy es la plataforma educativa de CIRP donde los usuarios pueden acceder a cursos y lecciones

INFORMACIÓN IMPORTANTE SOBRE LANDINGS DE CLIENTES:
- La tabla "usuarios" guarda la información de los landings que crean los clientes
- El campo "usuario" contiene el nombre del landing
- El campo "descripcion" contiene la descripción del landing
- Cada registro en la tabla "usuarios" representa un landing creado por un cliente

INFORMACIÓN IMPORTANTE SOBRE PIXELS DE CAMPAÑAS:
- La tabla "pixels" guarda la información de los pixels de los usuarios
- Contiene el código del pixel relacionado a la campaña
- Los pixels se usan para tracking y análisis de campañas publicitarias

DATOS ACTUALES DE LA BASE DE DATOS:
${contextInfo}

INSTRUCCIONES IMPORTANTES:
1. Si el usuario pregunta sobre datos específicos, usa EXACTAMENTE la información proporcionada en "DATOS ACTUALES DE LA BASE DE DATOS"
2. Procesa y presenta la información de manera clara y organizada con emojis y formato estructurado
3. Si hay datos numéricos (totales, conteos), preséntalos de forma destacada con emojis
4. Si el usuario pregunta "cuántos" o "cuántas", responde con números específicos de los datos
5. Si hay registros específicos, menciona los detalles más relevantes de forma estructurada
6. Mantén un tono profesional pero amigable
7. Si no hay datos disponibles, explica claramente por qué
8. Si el usuario ejecutó una consulta SQL, explica los resultados de manera comprensible
9. Para consultas SQL, interpreta los datos y proporciona insights útiles
10. Para cursos: menciona nombres específicos, fechas de creación y descripciones con formato estructurado
11. Para usuarios/landings: menciona nombres de usuarios y descripciones de landings con formato estructurado
12. Para pixels: menciona códigos de pixels y campañas relacionadas con formato estructurado

FORMATO DE RESPUESTA RECOMENDADO:
- Usa emojis para hacer la información más visual
- Organiza la información en secciones claras
- Destaca los números importantes
- Menciona ejemplos específicos cuando sea relevante
- Usa viñetas o numeración para listas

EJEMPLO DE RESPUESTA PARA CURSOS:
Si hay 37 cursos, di: "🎓 **CIRP Academy tiene 37 cursos disponibles** 📚

Algunos de los cursos más destacados incluyen:
• 🎯 **CONFIGURACIÓN Y BENEFICIOS DEL CIRP** - CURSO RECOMENDADO
• 📱 **CONFIGURANDO MI CTA EN YOUTUBE** - CURSO RECOMENDADO  
• 🎬 **SUBIENDO MIS VIDEOS CIRP A YOUTUBE** - CURSO RECOMENDADO
• 📱 **CREACION DE CONTENIDOS EN REDES SOCIALES** - CURSO RECOMENDADO
• 🎯 **POSICIONAMIENTO EN REDES SOCIALES** - CURSO RECOMENDADO

📅 La mayoría de los cursos fueron creados en julio 2025, mostrando el crecimiento reciente de la plataforma."

Usuario: ${inputMessage}

Responde basándote en los datos reales proporcionados. Si hay información específica en los datos, úsala para dar una respuesta precisa y detallada. Si se ejecutó una consulta SQL, interpreta los resultados y proporciona análisis útiles.`;

      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=AIzaSyCRuVAyyWKO2ip5JIl2AwnIK_yZXFjDc08`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contents: [{
            parts: [{
              text: prompt
            }]
          }],
          generationConfig: {
            temperature: 0.7,
            topK: 40,
            topP: 0.95,
            maxOutputTokens: 1024,
          }
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data); // Para debugging
      
      if (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts) {
        const aiResponse = {
          id: Date.now() + 1,
          text: data.candidates[0].content.parts[0].text,
          sender: 'ai',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, aiResponse]);
      } else {
        console.error('Estructura de respuesta inesperada:', data);
        throw new Error('Respuesta inválida de la API');
      }
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: `Lo siento, hubo un error al procesar tu mensaje: ${error.message}. Por favor, inténtalo de nuevo.`,
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="ai-chat-container">
      <Button
        className="ai-chat-toggle"
        type="primary"
        shape="circle"
        icon={<RobotOutlined />}
        size="large"
        onClick={() => setIsOpen(!isOpen)}
      />
      
      {isOpen && (
        <Card className="ai-chat-window" title="Asistente IA">
          <div className="messages-container">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}
              >
                <div className="message-content">
                  <Avatar
                    size="small"
                    icon={message.sender === 'user' ? <UserOutlined /> : <RobotOutlined />}
                    style={{
                      backgroundColor: message.sender === 'user' ? '#1890ff' : '#165afa'
                    }}
                  />
                  <div className="message-text">
                    <Text>{message.text}</Text>
                    <div className="message-time">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message ai-message">
                <div className="message-content">
                  <Avatar
                    size="small"
                    icon={<RobotOutlined />}
                    style={{ backgroundColor: '#165afa' }}
                  />
                  <div className="message-text">
                    <Spin size="small" />
                    <Text type="secondary">Escribiendo...</Text>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <div className="input-container">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Escribe tu mensaje..."
              disabled={isLoading}
              suffix={
                <Button
                  type="text"
                  icon={<SendOutlined />}
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                />
              }
            />
          </div>
        </Card>
      )}
    </div>
  );
};

export default AIChat;
